import { motion } from 'framer-motion';
import { CheckCircle2, Users, Globe2, Award } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { PageHero } from '@/components/Cards';
import { useLanguage } from '@/hooks/useLanguage';

const milestones = [
  {
    year: '2015',
    title: 'Company Founded',
    titleTh: 'ก่อตั้งบริษัท',
    desc: 'Yaruwat Co., Ltd. was established in Bangkok, Thailand, with a vision to bridge Thai and Chinese markets.',
    descTh: 'บริษัท ยารุวาท จำกัด ก่อตั้งขึ้นในกรุงเทพมหานคร ด้วยวิสัยทัศน์ในการเชื่อมต่อตลาดไทยและจีน',
  },
  {
    year: '2017',
    title: 'Medical Device Distribution',
    titleTh: 'จัดจำหน่ายอุปกรณ์การแพทย์',
    desc: 'Expanded into medical device distribution, partnering with leading global manufacturers.',
    descTh: 'ขยายสู่การจัดจำหน่ายอุปกรณ์การแพทย์ โดยร่วมมือกับผู้ผลิตชั้นนำระดับโลก',
  },
  {
    year: '2019',
    title: 'Thai Fruit Export to China',
    titleTh: 'ส่งออกผลไม้ไทยสู่จีน',
    desc: 'Obtained GACC registration for orchards and packing houses, beginning large-scale fruit exports to China.',
    descTh: 'ได้รับการขึ้นทะเบียน GACC สำหรับสวนผลไม้และโรงคัดบรรจุ เริ่มส่งออกผลไม้ขนาดใหญ่สู่จีน',
  },
  {
    year: '2021',
    title: 'Coffee Business Launch',
    titleTh: 'เปิดตัวธุรกิจกาแฟ',
    desc: 'Launched premium Thai coffee product line, sourcing from northern Thailand highland plantations.',
    descTh: 'เปิดตัวสายผลิตภัณฑ์กาแฟไทยพรีเมียม จากไร่ที่ราบสูงทางภาคเหนือของประเทศไทย',
  },
  {
    year: '2023',
    title: 'AI Live Streaming Division',
    titleTh: 'แผนกไลฟ์สดด้วย AI',
    desc: 'Pioneered AI-powered live streaming e-commerce to connect Thai products with millions of Chinese consumers.',
    descTh: 'บุกเบิกอีคอมเมิร์ซไลฟ์สดด้วย AI เพื่อเชื่อมต่อสินค้าไทยกับผู้บริโภคชาวจีนหลายล้านคน',
  },
];

const teamValues = [
  {
    icon: <CheckCircle2 className="w-6 h-6 text-primary" />,
    title: 'Integrity',
    titleTh: 'ความซื่อสัตย์',
    desc: 'We operate with transparency and ethical business practices in every transaction.',
    descTh: 'เราดำเนินงานด้วยความโปร่งใสและแนวทางธุรกิจที่มีจริยธรรมในทุกธุรกรรม',
  },
  {
    icon: <Globe2 className="w-6 h-6 text-primary" />,
    title: 'Global Perspective',
    titleTh: 'มุมมองระดับโลก',
    desc: 'Thai roots with an international mindset, bridging cultures and markets.',
    descTh: 'รากเหง้าไทยพร้อมความคิดระดับสากล เชื่อมต่อวัฒนธรรมและตลาด',
  },
  {
    icon: <Users className="w-6 h-6 text-primary" />,
    title: 'Partnership',
    titleTh: 'ความร่วมมือ',
    desc: 'Building long-term relationships with clients, suppliers, and communities.',
    descTh: 'สร้างความสัมพันธ์ระยะยาวกับลูกค้า ซัพพลายเออร์ และชุมชน',
  },
  {
    icon: <Award className="w-6 h-6 text-primary" />,
    title: 'Excellence',
    titleTh: 'ความเป็นเลิศ',
    desc: 'Committed to the highest quality standards in all products and services.',
    descTh: 'มุ่งมั่นในมาตรฐานคุณภาพสูงสุดในสินค้าและบริการทั้งหมด',
  },
];

export default function About() {
  const { t } = useLanguage();

  return (
    <Layout>
      <PageHero
        title="About Us"
        titleTh="เกี่ยวกับเรา"
        subtitle="Rooted in Thailand, Bridging China, Reaching the World"
        subtitleTh="รากเหง้าในไทย เชื่อมโยงจีน สู่โลกกว้าง"
        image="/images/hero_5.jpeg"
      />

      {/* Company Story */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-primary font-semibold text-sm uppercase tracking-widest">
                {t('Our Story', 'เรื่องราวของเรา')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-6">
                {t('A Thai Company with a Global Heart', 'บริษัทไทยที่มีหัวใจระดับโลก')}
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  {t(
                    'Yaruwat Co., Ltd. is a proudly Thai-majority owned trading company headquartered in Bangkok, Thailand. Founded with the vision of leveraging Thailand\'s natural and industrial strengths, we serve as a strategic bridge between the Thai and Chinese economies.',
                    'บริษัท ยารุวาท จำกัด เป็นบริษัทการค้าที่ถือหุ้นโดยคนไทยเสียงข้างมาก มีสำนักงานใหญ่ในกรุงเทพมหานคร ก่อตั้งขึ้นด้วยวิสัยทัศน์ในการใช้ประโยชน์จากจุดแข็งทางธรรมชาติและอุตสาหกรรมของประเทศไทย เราทำหน้าที่เป็นสะพานเชิงกลยุทธ์ระหว่างเศรษฐกิจไทยและจีน'
                  )}
                </p>
                <p>
                  {t(
                    'Our diverse portfolio spans medical devices, premium coffee products, fresh tropical fruit exports, and cutting-edge AI live streaming e-commerce — all united by our commitment to quality, compliance, and cultural understanding.',
                    'พอร์ตโฟลิโอที่หลากหลายของเราครอบคลุมอุปกรณ์การแพทย์ ผลิตภัณฑ์กาแฟพรีเมียม ส่งออกผลไม้เขตร้อนสด และอีคอมเมิร์ซไลฟ์สดด้วย AI — ทั้งหมดรวมกันด้วยความมุ่งมั่นด้านคุณภาพ การปฏิบัติตามข้อกำหนด และความเข้าใจทางวัฒนธรรม'
                  )}
                </p>
                <p>
                  {t(
                    'With Thai majority ownership and deep-rooted local knowledge, combined with our strategic partnership with Xianpinda in China, we offer unique advantages that few can match in the Thailand-China trade corridor.',
                    'ด้วยการถือหุ้นโดยคนไทยเสียงข้างมากและความรู้ท้องถิ่นที่ฝังรากลึก รวมกับความร่วมมือเชิงกลยุทธ์กับ Xianpinda ในประเทศจีน เราเสนอข้อได้เปรียบเฉพาะที่ไม่กี่บริษัทสามารถทำได้ในเส้นทางการค้าไทย-จีน'
                  )}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="/images/hero_1.jpeg"
                alt="Bangkok River"
                className="rounded-2xl shadow-xl w-full h-80 object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-primary text-primary-foreground rounded-2xl p-6 shadow-xl">
                <div className="text-3xl font-bold">🇹🇭</div>
                <div className="text-sm font-semibold mt-1">{t('Thai Majority Owned', 'ถือหุ้นโดยคนไทยเสียงข้างมาก')}</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-primary font-semibold text-sm uppercase tracking-widest">
              {t('Our Values', 'ค่านิยมของเรา')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2">
              {t('What Drives Us', 'สิ่งที่ขับเคลื่อนเรา')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamValues.map((val, i) => (
              <motion.div
                key={val.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-md border border-border/50"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  {val.icon}
                </div>
                <h3 className="font-bold text-foreground mb-2">{t(val.title, val.titleTh)}</h3>
                <p className="text-sm text-muted-foreground">{t(val.desc, val.descTh)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-primary font-semibold text-sm uppercase tracking-widest">
              {t('Our Journey', 'เส้นทางของเรา')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2">
              {t('Company Milestones', 'เหตุการณ์สำคัญของบริษัท')}
            </h2>
          </motion.div>

          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border" />
            <div className="space-y-8">
              {milestones.map((m, i) => (
                <motion.div
                  key={m.year}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex gap-6 pl-20 relative"
                >
                  <div className="absolute left-4 top-1 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold shadow-md">
                    {m.year.slice(2)}
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-border/50 flex-1">
                    <div className="text-primary text-sm font-semibold mb-1">{m.year}</div>
                    <h3 className="font-bold text-foreground mb-1">{t(m.title, m.titleTh)}</h3>
                    <p className="text-sm text-muted-foreground">{t(m.desc, m.descTh)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
